import { useEffect, useState, lazy, Suspense } from "react";
import "react-international-phone/style.css";
import "./style.scss";
import { PATH_LIST } from "shared/lib/react-router";
import { useTranslation } from "react-i18next";
import { KYC_Step } from "entities/account";
import { VerificationBtn } from "../components/button";
import PhoneNumber from "../../../../assets/verification/PhoneNumber.svg";
import Edit from "../../../../assets/verification/Edit.svg";
import PhoneNumberVerify from "../../../../assets/verification/PhoneNumberVerify.svg";
import { useVerificationStep } from "hooks/useVerificationStep";
import OtpInput from "widgets/otp-input";
import { useStore } from "zustand";
import { sessionModel } from "entities/session";
import { useNavigate } from "react-router-dom";
import { useCountriesFixed } from "./country-fix";
import { EntityType } from "static/types";
import Loading from "widgets/loading";

const LazyPhoneInput = lazy(() =>
  import("react-international-phone").then((mod) => ({
    default: mod.PhoneInput,
  }))
);

const isPhoneValidAsync = async (phone: string): Promise<boolean> => {
  try {
    const { PhoneNumberUtil } = await import("google-libphonenumber");
    const phoneUtil = PhoneNumberUtil.getInstance();
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const PhoneNumberPage = () => {
  const navigate = useNavigate();
  const { t: a } = useTranslation("app");
  const { t: y } = useTranslation("yup");
  const { t, i18n } = useTranslation("onboarding-auth");
  const { t: o } = useTranslation("onboarding");
  const { countriesFixed, isLoading: countriesLoading } = useCountriesFixed();

  const [phone, setPhone] = useState<string>("");
  const [phoneRegistered, setPhoneRegistered] = useState(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const [timer, setTimer] = useState(59);
  const [resend, setResend] = useState(false);

  const [result, setResult] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const { user, entity } = useStore(sessionModel.sessionStore);
  const { isLoading, onSubmit } =
    useVerificationStep<KYC_Step.PhoneVerification>({
      step: KYC_Step.PhoneVerification,
      navigateOnSuccess: "",
      onSuccess: () => {
        setPhoneRegistered(true);
        setResend(false);
        setTimer(59);
      },
      onError: !phoneRegistered
        ? undefined
        : () => {
            setError(true);
          },
    });

  useEffect(() => {
    if (user?.requestedPhone) setPhone("+" + user.requestedPhone);
    else if (user?.phone) setPhone("+" + user.phone);
    else setPhone("");
  }, [user?.requestedPhone, user?.phone]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!phone) return setIsValid(false);
      isPhoneValidAsync(phone).then(setIsValid);
    }, 300);

    return () => clearTimeout(timeout);
  }, [phone]);

  useEffect(() => {
    if (!resend && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    } else if (!resend && timer === 0) {
      setResend(true);
    }
  }, [timer, resend, phoneRegistered]);

  const handleOnChange = (newValue: string) => {
    setResult(newValue);
    setError(false);

    if (newValue.length === 4) {
      onSubmit(
        {
          phone,
          code: newValue,
        },
        entity === EntityType.Entity
          ? PATH_LIST.businessDocuments
          : PATH_LIST.uploadCredentials
      );
    }
  };

  const isFilled = result.length === 4;

  return (
    <div className="verification-phone-number">
      <div className="verification-phone-number-main">
        <div className="verification-phone-number-top-wrapper">
          <img
            src={phoneRegistered ? PhoneNumberVerify : PhoneNumber}
            alt="PhoneNumber"
          />
          <p>{phoneRegistered ? o("enterCode") : o("phoneNumber")}</p>
          <span>
            {phoneRegistered ? o("codeSentToPhone") : o("verificationCode")}
          </span>
          {phoneRegistered && (
            <p className="phone-number-and-edit">
              {phone}
              <img
                onClick={() => setPhoneRegistered(false)}
                src={Edit}
                alt="edit"
              />
            </p>
          )}
        </div>

        {phoneRegistered ? (
          <div className="verification-phone-number-field-wrapper">
            <OtpInput
              value={result}
              onChange={handleOnChange}
              disabled={isLoading}
              error={error}
              isFilled={isFilled}
              shouldAutoFocus={true}
            />

            {error && <span className="error-verify">{y("codeInvalid")}</span>}
            {timer > 0 && (
              <span className="resend-code">
                {t("resendCode")} {timer} {o("sec")}
              </span>
            )}
            {timer < 30 && (
              <span className="resend-code">
                {t("didntGetCode")}{" "}
                <span onClick={() => navigate(PATH_LIST.support)}>
                  {t("contactSupportOTP")}
                </span>
              </span>
            )}
          </div>
        ) : (
          <div className="verification-phone-number-field-wrapper">
            <Suspense fallback={<Loading />}>
              {!countriesLoading && (
                <LazyPhoneInput
                  className={`phone-input-class_${i18n.language}`}
                  countries={countriesFixed}
                  defaultCountry={"am"}
                  value={phone}
                  onChange={(v: string) => setPhone(v)}
                  inputProps={{ autoFocus: true }}
                />
              )}
            </Suspense>
            {phone.length > 7 && !isValid && (
              <p className="validation">{y("phoneNotValid")}</p>
            )}
          </div>
        )}
      </div>

      <VerificationBtn
        disabled={isLoading || (phoneRegistered ? !resend : !isValid)}
        onClick={() => {
          phone.replace(/\D/g, "") === user?.phone && user?.isPhoneVerified
            ? navigate(
                entity === EntityType.Entity
                  ? PATH_LIST.businessDocuments
                  : PATH_LIST.uploadCredentials
              )
            : onSubmit({
                phone,
                code: "",
              });
        }}
        text={phoneRegistered ? t("resendCode") : t("continue")}
      />
    </div>
  );
};
